import { get } from 'aws-amplify/api';
import { PRESCRIPTSURE } from '../constants/api-endpoints';
import { getSessionToken } from './service-config';

const apiNamePath = PRESCRIPTSURE.MMIT_COVERAGE_API;

/**
 * New unified coverage API service
 * Replaces separate Medicare and Commercial APIs
 */
const coverageApiService = {
  /**
   * Search for plans
   * @param {string} searchTerm - Search query for plans
   * @param {number} limit - Maximum number of results (default: 20)
   */
  async searchPlans(searchTerm, limit = 20) {
    try {
      const sessionToken = await getSessionToken();

      const searchPlansOperation = get({
        apiName: apiNamePath,
        path: `/search`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            search_term: searchTerm,
            entity_type: 'plans',
            limit: limit.toString(),
          },
        },
      });

      const result = await searchPlansOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error searching plans:', error);
      throw error;
    }
  },

  /**
   * Search for drugs/products
   * @param {string} searchTerm - Search query for drugs
   * @param {number} limit - Maximum number of results (default: 20)
   * @param {AbortSignal} signal - Optional abort signal for cancellation
   */
  async searchDrugs(searchTerm, limit = 20, signal = null) {
    try {
      const sessionToken = await getSessionToken();

      const searchDrugsOperation = get({
        apiName: apiNamePath,
        path: `/search`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            search_term: searchTerm,
            entity_type: 'products',
            limit: limit.toString(),
          },
          ...(signal && { abortSignal: signal }),
        },
      });

      const result = await searchDrugsOperation.response;
      return result.body.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        return null; // Request was cancelled
      }
      console.error('Error searching drugs:', error);
      throw error;
    }
  },

  /**
   * Get coverage information for a specific plan and product
   * @param {number} planId - Plan ID
   * @param {number} productId - Product ID
   */
  async getCoverage(planId, productId) {
    try {
      const sessionToken = await getSessionToken();

      const getCoverageOperation = get({
        apiName: apiNamePath,
        path: `/formulary_coverage`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            coverage_call: 'get_plan_coverage',
            product_id: productId.toString(),
            plan_id: planId.toString(),
          },
        },
      });

      const result = await getCoverageOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting coverage:', error);
      throw error;
    }
  },

  /**
   * Get indications for a product
   * @param {number} productId - Product ID
   */
  async getIndicationsByProduct(productId) {
    try {
      const sessionToken = await getSessionToken();

      const getIndicationsOperation = get({
        apiName: apiNamePath,
        path: `/formulary_indications`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            indication_call: 'get_indications_by_product',
            product_id: productId.toString(),
          },
        },
      });

      const result = await getIndicationsOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting indications:', error);
      throw error;
    }
  },

  /**
   * Get alternative coverage for a plan and product
   * @param {number} planId - Plan ID
   * @param {number} productId - Product ID
   * @param {string} options - Options (e.g., 'CoveredOnly')
   */
  async getAlternativeCoverage(planId, productId, options = 'CoveredOnly') {
    try {
      const sessionToken = await getSessionToken();

      const getAlternativesOperation = get({
        apiName: apiNamePath,
        path: `/formulary_coverage`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            coverage_call: 'get_alternative_coverage_by_plan',
            product_id: productId.toString(),
            plan_id: planId.toString(),
            options: options,
          },
        },
      });

      const result = await getAlternativesOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting alternative coverage:', error);
      throw error;
    }
  },

  /**
   * Get therapeutic alternatives for a product
   * @param {number} productId - Product ID
   */
  async getTherapeuticAlternatives(productId) {
    try {
      const sessionToken = await getSessionToken();

      const getTherapeuticOperation = get({
        apiName: apiNamePath,
        path: `/formulary_coverage`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            coverage_call: 'get_product_alternatives',
            product_id: productId.toString(),
          },
        },
      });

      const result = await getTherapeuticOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting therapeutic alternatives:', error);
      throw error;
    }
  },

  /**
   * Get standard criteria for an indication
   * @param {string} indicationId - Indication ID
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getStandardCriteria(indicationId, productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getStandardCriteriaOperation = get({
        apiName: apiNamePath,
        path: `/formulary_indications`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            indication_call: 'get_standard_criteria',
            indication_id: indicationId,
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getStandardCriteriaOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting standard criteria:', error);
      throw error;
    }
  },

  /**
   * Get class criteria for an indication
   * @param {string} indicationId - Indication ID
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getClassCriteria(indicationId, productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getClassCriteriaOperation = get({
        apiName: apiNamePath,
        path: `/formulary_indications`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            indication_call: 'get_class_criteria',
            indication_id: indicationId,
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getClassCriteriaOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting class criteria:', error);
      throw error;
    }
  },

  /**
   * Get coverage notes
   * @param {number} noteId - Note ID
   */
  async getCoverageNotes(noteId) {
    try {
      const sessionToken = await getSessionToken();

      const getCoverageNotesOperation = get({
        apiName: apiNamePath,
        path: `/formulary_documents`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            document_call: 'get_coverage_notes',
            note_id: noteId.toString(),
          },
        },
      });

      const result = await getCoverageNotesOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting coverage notes:', error);
      throw error;
    }
  },

  /**
   * Get PA structured notes
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getPAStructuredNotes(productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getPANotesOperation = get({
        apiName: apiNamePath,
        path: `/formulary_documents`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            document_call: 'get_structured_notes_pa',
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getPANotesOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting PA structured notes:', error);
      throw error;
    }
  },

  /**
   * Get ST structured notes
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getSTStructuredNotes(productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getSTNotesOperation = get({
        apiName: apiNamePath,
        path: `/formulary_documents`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            document_call: 'get_structured_notes_st',
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getSTNotesOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting ST structured notes:', error);
      throw error;
    }
  },

  /**
   * Get medical coverage for plan
   * @param {number} productId - Product ID
   * @param {number} planId - Plan ID
   * @param {number} indicationId - Indication ID
   */
  async getMedicalCoverage(productId, planId, indicationId) {
    try {
      const sessionToken = await getSessionToken();

      const getMedicalCoverageOperation = get({
        apiName: apiNamePath,
        path: `/medical`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            medical_call: 'get_medical_coverage_for_plan',
            product_id: productId.toString(),
            plan_id: planId.toString(),
            indication_id: indicationId.toString(),
          },
        },
      });

      const result = await getMedicalCoverageOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting medical coverage:', error);
      throw error;
    }
  },

  /**
   * Get medical standard criteria
   * @param {string} indicationId - Indication ID
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getMedicalStandardCriteria(indicationId, productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getMedicalStandardOperation = get({
        apiName: apiNamePath,
        path: `/medical`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            medical_call: 'get_medical_standard_criteria_by_formulary',
            indication_id: indicationId,
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getMedicalStandardOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting medical standard criteria:', error);
      throw error;
    }
  },

  /**
   * Get medical class criteria
   * @param {string} indicationId - Indication ID
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getMedicalClassCriteria(indicationId, productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getMedicalClassOperation = get({
        apiName: apiNamePath,
        path: `/medical`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            medical_call: 'get_medical_class_criteria_by_formulary',
            indication_id: indicationId,
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getMedicalClassOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting medical class criteria:', error);
      throw error;
    }
  },

  /**
   * Get medical notes by product
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   * @param {number} indicationId - Indication ID
   */
  async getMedicalNotesByProduct(productId, formularyId, indicationId) {
    try {
      const sessionToken = await getSessionToken();

      const getMedicalNotesOperation = get({
        apiName: apiNamePath,
        path: `/medical`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            medical_call: 'get_medical_notes_by_product',
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
            indication_id: indicationId.toString(),
          },
        },
      });

      const result = await getMedicalNotesOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting medical notes:', error);
      throw error;
    }
  },

  /**
   * Get formulary standard criteria
   * @param {string} indicationId - Indication ID
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getFormularyStandardCriteria(indicationId, productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getFormularyStandardOperation = get({
        apiName: apiNamePath,
        path: `/formulary_indications`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            indication_call: 'get_standard_criteria',
            indication_id: indicationId,
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getFormularyStandardOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting formulary standard criteria:', error);
      throw error;
    }
  },

  /**
   * Get formulary class criteria
   * @param {string} indicationId - Indication ID
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getFormularyClassCriteria(indicationId, productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getFormularyClassOperation = get({
        apiName: apiNamePath,
        path: `/formulary_indications`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            indication_call: 'get_class_criteria',
            indication_id: indicationId,
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getFormularyClassOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting formulary class criteria:', error);
      throw error;
    }
  },

  /**
   * Get formulary document links
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   * @param {number} indicationId - Indication ID
   */
  async getFormularyDocumentLinks(productId, formularyId, indicationId) {
    try {
      const sessionToken = await getSessionToken();

      const getDocumentLinksOperation = get({
        apiName: apiNamePath,
        path: `/formulary_documents`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            document_call: 'get_document_links',
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
            indication_id: indicationId.toString(),
          },
        },
      });

      const result = await getDocumentLinksOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting formulary document links:', error);
      throw error;
    }
  },

  /**
   * Get formulary documents list
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   * @param {number} indicationId - Indication ID
   */
  async getFormularyDocumentsList(productId, formularyId, indicationId) {
    try {
      const sessionToken = await getSessionToken();

      const getDocumentsListOperation = get({
        apiName: apiNamePath,
        path: `/formulary_documents`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            document_call: 'get_pharmacy_documents',
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
            indication_id: indicationId.toString(),
          },
        },
      });

      const result = await getDocumentsListOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting formulary documents list:', error);
      throw error;
    }
  },

  /**
   * Get formulary document by ID
   * @param {string} documentId - Document ID
   */
  async getFormularyDocumentById(documentId) {
    try {
      const sessionToken = await getSessionToken();

      const getDocumentOperation = get({
        apiName: apiNamePath,
        path: `/formulary_documents`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            document_call: 'get_document_by_id',
            document_id: documentId,
          },
        },
      });

      const result = await getDocumentOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting formulary document by ID:', error);
      throw error;
    }
  },

  /**
   * Get medical document links
   * @param {number} indicationId - Indication ID
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getMedicalDocumentLinks(indicationId, productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getDocumentLinksOperation = get({
        apiName: apiNamePath,
        path: `/medical`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            medical_call: 'get_medical_document_links',
            indication_id: indicationId.toString(),
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getDocumentLinksOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting medical document links:', error);
      throw error;
    }
  },

  /**
   * Get medical documents list
   * @param {number} indicationId - Indication ID
   * @param {number} productId - Product ID
   * @param {number} formularyId - Formulary ID
   */
  async getMedicalDocumentsList(indicationId, productId, formularyId) {
    try {
      const sessionToken = await getSessionToken();

      const getDocumentsListOperation = get({
        apiName: apiNamePath,
        path: `/medical`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            medical_call: 'get_medical_documents',
            indication_id: indicationId.toString(),
            product_id: productId.toString(),
            formulary_id: formularyId.toString(),
          },
        },
      });

      const result = await getDocumentsListOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting medical documents list:', error);
      throw error;
    }
  },

  /**
   * Get medical document by ID
   * @param {string} documentId - Document ID
   */
  async getMedicalDocumentById(documentId) {
    try {
      const sessionToken = await getSessionToken();

      const getDocumentOperation = get({
        apiName: apiNamePath,
        path: `/medical`,
        options: {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          queryParams: {
            medical_call: 'get_medical_document_by_id',
            document_id: documentId,
          },
        },
      });

      const result = await getDocumentOperation.response;
      return result.body.json();
    } catch (error) {
      console.error('Error getting medical document by ID:', error);
      throw error;
    }
  },
};

export default coverageApiService;
